import {
  Flex,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Text,
  Box,
} from '@chakra-ui/react';
import Select from 'react-select';
import { components } from 'react-select';
import { useState, useMemo, useRef, useEffect } from 'react';
import { NoOptionsMessage } from '@/components/select';
import { capitalizeFirstLetter } from '@/utils/commonHelpers';
import { EnrichmentInfo } from '@/components/flow/EnrichmentInfo';
import { useProjectActionConfig } from './useProjectActionConfig';
import { BulkError } from '@/components/bulk-action/Error';
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { FormBuilder } from '@/components/formBuilder';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAddCandidateMutation } from '@/store/query/mco-candidates';
import {
  getProjectDetails,
  projectDetailsActions,
  projectDetailsApiActions,
} from '@/store/projectDetails';
import { useProjectAccess } from '@/hooks/useProjectAccess';
import { SendForReviewV1 } from './sendForReviewV1';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';
import { KText } from '@/ui-library/components/text';
import { useGetInternalTeamProjectsQuery } from '@/store/query/projects';
import {
  SelectOptionWithAvatar,
  SelectWithDescriptionOption,
} from '@/components/formBuilder/SelectComponents';
import { useShowToast } from '@/hooks/useShowToast';
import { useKFlags } from '@/hooks/use-k-flags';
import { useBulkOperation } from '@/components/ats/BulkOperationQueue';
import { useQueryClient } from '@tanstack/react-query';
import { PROJECT_PROSPECTS_QUERY_KEY } from '@/api/useGetProjectProspects';
import { isValidEmail } from '@/constants/regex';

export const ProjectOptions = ({
  modalActions,
  action = '',
  selected = [],
  toggleRowSelected = () => {},
  projectIdProp = null,
  fetchInBg = () => {},
  fnHandleAction = () => {},
  toggleAllRowsSelected = () => {},
  inputText = '',
  // Only true when triggered from the checkbox bulk-action bar. Single-row
  // (per-prospect) actions stay on the synchronous path (no is_bulk_activity).
  isBulkActivity = false,
}) => {
  const actionConfig = useProjectActionConfig(action, selected);
  const { currentProject: { data } = {} } = useSelector(getProjectDetails);
  const { allowedCollaboratorsOptionsForCreateReview } = useProjectAccess(data);
  const {
    currentProject: {
      data: { access },
    },
    statusFilter,
  } = useSelector(getProjectDetails);

  const [addCandidate] = useAddCandidateMutation();
  const removeBulkOp = useBulkOperation({
    activityType: 'remove_prospect',
    title: 'Removing candidates from project',
  });
  const reviewBulkOp = useBulkOperation({
    activityType: 'project_send_review',
    title: 'Sending candidates for review',
  });
  const { isOpen, onClose } = modalActions;
  const [isActionLoadingState, setActionProgress] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [errorIds, setErrorIds] = useState([]);
  const [userInput, setUserInput] = useState(inputText || '');
  const [selectedInput, setSelectedInput] = useState(undefined);
  const isExternalUser = useIsExternalUser();
  const queryClient = useQueryClient();
  const { applyStatusFilter } = projectDetailsActions;
  const reviewSelected =
    action === 'review'
      ? selected.filter((item) => !item?.current_review)
      : selected;
  const dispatch = useDispatch();
  const { ats_enabled } = useKFlags();
  const {
    title,
    description,
    successButtonText,
    placeholder,
    validator = () => {},
    renderType = 'input',
    options = [],
    isLoading,
    isMulti,
    searchTitle,
    isActionLoading = false,
    queryAction = () => {},
    actionSuccessMessage = '',
    actionErrorMessage = '',
    apiConfig = {},
  } = actionConfig;

  const formRef = useRef();
  const selectedReviewerRef = useRef();
  const { projectId } = useParams();
  const toast = useShowToast();

  const { data: internalUsers, isLoading: isinternalUsersLoading } =
    useGetInternalTeamProjectsQuery(
      {
        projectId: data?.id,
      },
      // Only the review form needs the internal-team list; skip the request for
      // remove/add so opening those modals (esp. with 100 selected) stays fast.
      { skip: !data?.id || action !== 'review' }
    );
  const internalUsersOptions =
    internalUsers?.map((item) => ({
      label: item.name,
      value: item.id,
      ...item,
    })) || [];

  useEffect(() => {
    setActionProgress(isActionLoading);
  }, [isActionLoading]);

  const showToast = ({ title = '', description = '', status = 'info' }) =>
    toast({
      title: title,
      description: description,
      position: 'bottom-left',
      status: status,
      duration: 4000,
      isClosable: true,
    });

  const fetchProjectProspects = () => {
    const payload = { id: statusFilter?.id || statusFilter?.internalDefault };
    dispatch(applyStatusFilter(payload));
    // Prospects list lives in TanStack Query now — invalidate so it refetches.
    queryClient.invalidateQueries({ queryKey: [PROJECT_PROSPECTS_QUERY_KEY] });
  };

  const handleAction = () => {
    switch (action) {
      case 'remove':
        handleApiCalls();
        break;
      case 'add':
        handleApiCalls();
        break;
      case 'review':
        if (formRef.current) {
          formRef.current.handleSubmit();
        }
        break;
      case 'clone':
        handleApiQuerys();
        break;
      case 'archive':
        handleApiQuerys();
        break;
      case 'remove_member':
        fnHandleAction();
        break;
      case 'transfer_ownership':
        fnHandleAction();
        break;
      case 'flow':
        onAddToFlow();
        break;

      default:
        console.log('no valid actions provided');
    }
    if (action === 'error') {
      setErrorIds([]);
    }
  };

  const handleApiQuerys = async () => {
    try {
      const payload = { projectId: projectIdProp || projectId };
      if (action === 'clone') {
        payload.name = userInput;
      }
      const result = await queryAction(payload);
      if (result?.data?.id) {
        showToast({ title: actionSuccessMessage, status: 'success' });
        if (action === 'clone' && !projectIdProp) {
          window.open(
            `${window.location.origin}/projects/${result?.data?.id}`,
            '_self'
          );
          return;
        }
        if (action === 'archive' && !projectIdProp) {
          window.open(`${window.location.href}`, '_self');
          return;
        }
        if (projectIdProp) {
          fetchInBg();
        }
        handleOnClose();
      } else {
        showToast({
          title: actionErrorMessage,
          status: 'error',
          description: result?.error?.[0],
        });
      }
    } catch {
      // ignore — failure is already surfaced to the user via the toast above
    }
  };

  const onAddToFlow = async () => {
    setActionProgress(true);
    try {
      const result = await addCandidate({
        outreachId: selectedInput?.id,
        contacts: selected?.map((item) => item?.contact?.id),
      });
      if (result?.error?.length) {
        if (result?.error?.length !== selected?.length) {
          fetchProjectProspects();
        }
        setErrorIds(result?.error);
        return;
      }
      toast({
        title: 'Contact added to flow.',
        position: 'bottom-left',
        status: 'success',
        isClosable: true,
      });
      fetchProjectProspects();
      handleOnClose();
    } catch {
      // ignore — loading state is always reset in finally
    } finally {
      setActionProgress(false);
    }
  };

  const handleApiCalls = async (values = {}) => {
    const config = { ...values };
    let updatedConfig = config;
    if (!apiConfig) return;
    const { urlPath, apiAction, contactField } = apiConfig;
    const urlPaths = { projectId };
    let requestConfig = {};
    urlPaths[urlPath] = urlPath;
    requestConfig[contactField] = reviewSelected.map((item) =>
      action === 'review' ? item?.id : item?.contact?.id
    );
    const isUnverifiedUser =
      typeof config?.user_id === 'string' && config?.user_id?.includes('--*--');

    if (action === 'review') {
      const emailCheck = config?.reviewer_id || config?.user_id;
      if (config?.requester_message) {
        config.requester_message = config.requester_message?.replaceAll(
          '\n',
          '<br>'
        );
      }
      const isCustomEmail =
        typeof emailCheck === 'string' &&
        (typeof config?.user_id === 'string'
          ? !config?.user_id?.includes('--*--')
          : true);
      if (isUnverifiedUser) {
        const [userId, currentFormKey] = config.user_id.split('--*--');
        const intValue = parseInt(userId, 10);
        updatedConfig[currentFormKey] = intValue;
        delete requestConfig.user_id;
      }
      updatedConfig = isCustomEmail ? { email: emailCheck } : config;
    }
    if (action === 'add') {
      urlPaths.projectId = selectedInput?.id;
    }
    requestConfig = { ...requestConfig, ...updatedConfig };
    if (isUnverifiedUser) {
      delete requestConfig.user_id;
    }

    // Route remove / send-for-review through the async bulk-activity queue —
    // but only for checkbox bulk actions. Single-row actions fall through to
    // the synchronous path below (no is_bulk_activity).
    if (isBulkActivity && (action === 'remove' || action === 'review')) {
      const bulkOp = action === 'review' ? reviewBulkOp : removeBulkOp;
      const total = reviewSelected?.length || 0;
      requestConfig.is_bulk_activity = true;
      bulkOp.startTracking(total);
      handleOnClose();
      try {
        const bulkResponse = await dispatch(
          projectDetailsApiActions(apiAction, {
            urlPaths,
            requestConfig,
            isApi: true,
          })
        );
        if (bulkResponse?.errors) {
          bulkOp.completeFailed();
          return;
        }
        bulkOp.completeSuccess(total);
      } catch {
        bulkOp.completeFailed();
      }
      return;
    }

    const response = await dispatch(
      projectDetailsApiActions(apiAction, {
        urlPaths,
        requestConfig,
        isApi: true,
      })
    );

    if (action === 'review') {
      if (response?.errors?.reviews?.length > 0) {
        const errorMsg = response?.errors?.reviews?.map((error) => {
          const currentProspect = reviewSelected?.find(
            (item) => item?.id === error?.prospect_id
          );
          return {
            ...error,
            id: currentProspect?.contact?.id,
            message: error?.errors?.base[0],
          };
        });
        setErrorIds(errorMsg);
        return;
      }
      const alreadyPresentError =
        response?.errors?.project_collaborator?.errors?.account_invitation;

      if (alreadyPresentError?.length > 0) {
        const { email, errors } = alreadyPresentError[0];
        if (email || errors) {
          showToast({
            title: `${email} - ${errors?.email[0]}`,
            status: 'error',
          });
        } else if (alreadyPresentError[0] === 'err_invitation_already_exists') {
          showToast({
            title: `Invitation already exists for this email. Please wait until the user signs up.`,
            status: 'error',
          });
        }
        if (alreadyPresentError?.length !== reviewSelected?.length) {
          fetchProjectProspects();
        }
        return;
      }
      if (response?.errors?.user_id?.length > 0) {
        const {
          errors: { user_id },
        } = response;
        showToast({ title: `user ${user_id[0]}`, status: 'error' });
        if (response?.errors?.user_id?.length !== reviewSelected?.length) {
          fetchProjectProspects();
        }
        return;
      }
      if (response?.errors?.length > 0) {
        const { errors } = response;
        showToast({ title: `${errors[0]}`, status: 'error' });
        return;
      }
      if (response?.errors?.email?.length > 0) {
        showToast({
          title: `Email ${response?.errors?.email[0]}`,
          status: 'error',
        });
        return;
      }
    } else if (response?.errors?.length > 0) {
      setErrorIds(response?.errors);
      return;
    }
    showToast({ title: actionSuccessMessage, status: 'success' });
    fetchProjectProspects();
    if (action === 'review') {
      dispatch(
        projectDetailsApiActions('fetchProjectById', {
          urlPaths: { projectId },
        })
      );
    }
    handleOnClose(response);
  };

  const handleInputChange = (value) => {
    setUserInput(value);
  };

  const isButtonDisabled = useMemo(() => {
    let val;
    if (renderType === 'select') val = selectedInput;
    if (renderType === 'input') val = userInput;
    if (renderType === 'textArea') val = userInput;
    if (renderType === 'requestReview') return false;
    return !validator(val);
  }, [renderType, selectedInput, userInput, validator]);

  const handleOnClose = (response = {}) => {
    const errorLength = response?.errors?.length || 1;
    onClose();
    setUserInput('');
    setSelectedInput(undefined);
    if (errorLength !== reviewSelected?.length) {
      fetchProjectProspects();
    }
  };

  const renderSelect = () => {
    return (
      <>
        <Box>
          {action === 'flow' && (
            <Box mb="20px">
              <EnrichmentInfo />
            </Box>
          )}
          <KText size="sm-bold" color="secondary-900" mb="0.5rem">
            {searchTitle}
          </KText>
          <Box mt="4px">
            <Select
              isMulti={isMulti}
              options={options}
              placeholder={placeholder}
              styles={KSelectStylesConfig({})}
              backspaceRemovesValue={false}
              isLoading={isLoading}
              components={{
                IndicatorSeparator: null,
                NoOptionsMessage,
              }}
              onChange={setSelectedInput}
              getOptionLabel={(option) =>
                option?.title || option?.label || option?.name
              }
              getOptionValue={(option) => option.id}
            />
          </Box>
        </Box>
      </>
    );
  };

  const renderInputCount = () => {
    return (
      <Flex flexDir="row-reverse" color="secondary-700" fontSize="sm">
        {userInput?.length || 0}/60
      </Flex>
    );
  };

  const renderInput = () => {
    return (
      <Input
        variant="outline"
        _hover={{ borderColor: 'secondary-300', boxShadow: 'none' }}
        _focus={{
          borderColor: 'secondary-300',
          boxShadow: 'none',
          outline: 'none',
        }}
        maxLength={action === 'clone' ? 60 : undefined}
        placeholder={placeholder}
        value={userInput}
        onChange={(e) => {
          handleInputChange(e.target.value);
        }}
      />
    );
  };

  const renderUserList = (currentUser) => {
    const { contact, tableId } = currentUser || {};
    const { name, id, avatar, email } = contact || {};
    return (
      <Flex key={id} padding="6px 0" alignItems="center">
        <Avatar
          src={avatar}
          name={name}
          width="20px"
          height="20px"
          fontSize={'10px !important'}
          borderRadius="6px"
        />
        <Flex flexDir="column" ml="12px" flex="1">
          <KText size="sm-medium">{name}</KText>
          <KText>{email}</KText>
        </Flex>
        <Flex
          onClick={() => {
            toggleRowSelected(tableId, false);
          }}
          cursor="pointer"
          title={`Clear ${name} from selection`}
        >
          <KText
            fontWeight="semiBold"
            mr="3px"
            textAlign="center"
            color={'#C03744'}
          >
            Remove
          </KText>
        </Flex>
      </Flex>
    );
  };

  const requestReviewFormConfig = [
    {
      name: 'user_id',
      label: 'Send to',
      type: 'select',
      isRequired: true,
      options: internalUsersOptions,
      isLoading: isinternalUsersLoading,
      customFormatLabel: SelectOptionWithAvatar,
      placeholder: 'Select the reviewer',
      valueKey: 'id',
      labelKey: 'name',
      customOnChange: (value) => {
        selectedReviewerRef.current = value;
      },
      components: {
        Option: SelectWithDescriptionOption,
      },
      showAvatar: true,
      descKey: 'email',
      avatarKey: 'avatar',
      isNearLabel: true,
    },
    {
      name: 'requester_message',
      label: 'Add your message',
      type: 'textArea',
      isRequired: false,
      placeholder: 'Please add your message',
    },
    {
      type: 'custom',
      Field: ({ formik }) => {
        useEffect(() => {
          setIsFormDirty(formik?.isValid && formik?.dirty);
        }, [formik]);
      },
    },
  ];

  const sendForReviewV1 = () => {
    return (
      <SendForReviewV1
        modalActions={modalActions}
        action={action}
        selected={selected}
        toggleRowSelected={toggleRowSelected}
        toggleAllRowsSelected={toggleAllRowsSelected}
        projectIdProp={projectIdProp}
        fetchInBg={fetchInBg}
        fnHandleAction={fnHandleAction}
        inputText={inputText}
        requestReviewFormConfig={requestReviewFormConfig}
        selectedReviewerRef={selectedReviewerRef}
        isFormDirty={isFormDirty}
        isBulkActivity={isBulkActivity}
      />
    );
  };
  const renderBeforeContents = () => {
    return (
      <>
        {action === 'clone' && (
          <KText size="sm-bold" color="secondary-900" mb="0.5rem">
            Project Name:
          </KText>
        )}
      </>
    );
  };

  const renderAfterContents = () => {
    return (
      <>
        {['status', 'remove', 'review', 'add'].includes(action) && (
          <>
            <Box m="12px 0">
              <Text p="5px 0" fontWeight="semiBold">
                Candidates to {capitalizeFirstLetter(action)}{' '}
                {`(${reviewSelected?.length})`}
              </Text>
              <Box mt="5px" maxH="250px" overflow="auto" gap="5px">
                {reviewSelected.map(renderUserList)}
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  const renderRequestReviewForm = () => {
    let formKey;
    if (access === 'all_users') {
      formKey = 'user_id';
    } else {
      formKey = 'reviewer_id';
    }
    const requestReviewFormConfig = [
      {
        name: formKey,
        label: 'Send to',
        type: 'selectWithCreate',
        isRequired: true,
        options: allowedCollaboratorsOptionsForCreateReview.map((user) => {
          const {
            name,
            email,
            id,
            collabId,
            isUnverifiedExternalUser,
            currentFormKey,
          } = user;
          return {
            label: `${name} -*- ${email}`,
            value: access === 'all_users' ? id : collabId || id,
            email,
            isUnverifiedExternalUser,
            currentFormKey,
          };
        }),
        customFormatLabel: (data = {}) => {
          const { label } = data;
          const [name, email] = label
            ? label.split('-*-').map((str) => str.trim())
            : [null, null];
          return (
            <Box>
              <Text as="span" fontWeight="bold" fontSize="14px">
                {name}
              </Text>
              <Text
                as="span"
                pl="15px"
                size="sm"
                fontWeight="medium"
                variant="muted"
              >
                {' '}
                {email}
              </Text>
            </Box>
          );
        },
        customNoOptionsMessage: (props) => {
          const searchTerm = props?.selectProps?.inputValue;
          const searchTermLen = searchTerm.length;
          return (
            <components.NoOptionsMessage {...props}>
              <Box
                fontSize="14px"
                borderColor="border-color"
                color="secondary-700"
                textAlign="left"
              >
                {searchTermLen < 2
                  ? 'Please type 2 or more letters'
                  : 'Enter valid email to add as new option'}
              </Box>
            </components.NoOptionsMessage>
          );
        },
        validateNewOption: (email) => {
          return isValidEmail(email);
        },
        placeholder: 'Select the reviewer',
      },
      {
        name: 'requester_message',
        label: 'Add your message',
        type: 'textArea',
        isRequired: false,
        placeholder: 'Please add your message',
      },
    ];

    return (
      <FormBuilder
        config={requestReviewFormConfig}
        onSubmit={handleApiCalls}
        formRef={formRef}
        initialValues={{
          requester_message:
            'Kindly review these profiles and let me know if they seem like a good fit for the role.',
        }}
      />
    );
  };

  const renderAction = () => {
    return (
      <>
        {renderBeforeContents()}
        {renderType === 'input' && renderInput()}
        {renderType === 'input' && action === 'clone' && renderInputCount()}
        {renderType === 'select' && renderSelect()}
        {renderType === 'requestReview' && renderRequestReviewForm()}
        {!isExternalUser && renderAfterContents()}
      </>
    );
  };

  const renderError = () => {
    if (errorIds?.length === 0) return null;
    return <BulkError contacts={errorIds} />;
  };

  if (ats_enabled && action === 'review') {
    return sendForReviewV1();
  }

  return (
    <>
      <Modal isCentered isOpen={isOpen} onClose={handleOnClose}>
        <ModalOverlay />
        <ModalContent
          minW={
            ['add', 'remove', 'review'].includes(action) ? '600px' : '100px'
          }
        >
          <ModalHeader mb={'-6px'}>
            <KText size="md-bold" color="secondary-900">
              {' '}
              {title}
            </KText>
            {description && (
              <KText size="sm-medium" color="secondary-700">
                {description}
              </KText>
            )}
          </ModalHeader>
          <ModalBody>{renderAction()}</ModalBody>
          <ModalFooter>
            <Button variant="secondary" mr={3} onClick={handleOnClose}>
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={isButtonDisabled || isActionLoadingState}
              isLoading={isActionLoadingState}
              loadingText="Updating"
            >
              {capitalizeFirstLetter(successButtonText)}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {renderError()}
    </>
  );
};
